/**
 * @file Components/Admin/Tours.jsx
 * @description Admin tour management component with full CRUD,
 * new category/pricing/itinerary fields, drag-and-drop itinerary builder,
 * and showOnHomepage toggle for controlling homepage display.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Search, GripVertical, X, MapPin, Loader2, Star, DollarSign, Home } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllTours, createTour, updateTour, deleteTour } from '../../services/api';
import ImageUploader, { MultiImageUploader } from '../Ui/ImageUploader';

const CATEGORIES = [
  { value: 'customized', label: 'Customized' },
  { value: 'educational', label: 'Educational' },
  { value: 'adventurous', label: 'Adventurous' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'family', label: 'Family' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'adventure', label: 'Adventure (legacy)' },
  { value: 'cultural', label: 'Cultural (legacy)' },
  { value: 'beach', label: 'Beach (legacy)' },
  { value: 'mountain', label: 'Mountain (legacy)' },
  { value: 'wildlife', label: 'Wildlife (legacy)' },
  { value: 'city', label: 'City (legacy)' },
];

const ICONS = ['MapPin', 'Sunrise', 'Mountain', 'Camera', 'Utensils', 'Bed', 'Compass', 'Flag', 'Bus', 'Ship'];

const emptyForm = {
  title: '', description: '', price: '', duration: '', groupSize: '',
  location: '', category: 'customized', difficulty: 'moderate', featured: false, showOnHomepage: false,
  thumbnail: '', images: [], startDates: [''],
  highlights: [''], included: [''], notIncluded: [''],
  pricing: { solo: '', group: '', minGroupSize: 10 },
  itinerary: [],
  details: { maxCapacity: '', gearChecklist: [''], safetyProtocols: [''], learningOutcomes: [''], studentTeacherRatio: '', amenities: [''] },
};

// ─── Drag-and-Drop Itinerary Builder ───────────────────────────────────
const ItineraryBuilder = ({ itinerary, onChange }) => {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const addDay = () => {
    const nextDay = itinerary.length + 1;
    onChange([...itinerary, { day: nextDay, title: '', description: '', location: { name: '', coords: [] }, icon: 'MapPin' }]);
  };

  const removeDay = (index) => {
    const updated = itinerary.filter((_, i) => i !== index).map((d, i) => ({ ...d, day: i + 1 }));
    onChange(updated);
  };

  const updateDay = (index, field, value) => {
    const updated = [...itinerary];
    if (field.startsWith('location.')) {
      const subField = field.split('.')[1];
      updated[index] = { ...updated[index], location: { ...updated[index].location, [subField]: value } };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    onChange(updated);
  };

  const handleDragStart = (index) => { dragItem.current = index; };
  const handleDragEnter = (index) => { dragOverItem.current = index; };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const items = [...itinerary];
    const [removed] = items.splice(dragItem.current, 1);
    items.splice(dragOverItem.current, 0, removed);
    const reordered = items.map((d, i) => ({ ...d, day: i + 1 }));
    onChange(reordered);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700">Day-by-Day Itinerary</label>
        <button type="button" onClick={addDay} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
          <Plus size={14} /> Add Day
        </button>
      </div>
      {itinerary.map((day, index) => (
        <div
          key={index}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragEnter={() => handleDragEnter(index)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => e.preventDefault()}
          className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-move hover:border-indigo-300 transition-colors"
        >
          <div className="flex items-center gap-1 pt-2 text-gray-400 flex-shrink-0">
            <GripVertical size={16} />
            <span className="text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center">{day.day}</span>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-2">
            <input type="text" placeholder="Day title *" value={day.title}
              onChange={(e) => updateDay(index, 'title', e.target.value)}
              className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500 outline-none" />
            <textarea placeholder="Description" value={day.description} rows={2}
              onChange={(e) => updateDay(index, 'description', e.target.value)}
              className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm resize-none focus:ring-1 focus:ring-indigo-500 outline-none" />
            <input type="text" placeholder="Location name" value={day.location?.name || ''}
              onChange={(e) => updateDay(index, 'location.name', e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm outline-none" />
            <select value={day.icon || 'MapPin'} onChange={(e) => updateDay(index, 'icon', e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm">
              {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
            </select>
          </div>
          <button type="button" onClick={() => removeDay(index)} className="text-red-400 hover:text-red-600 pt-2 flex-shrink-0">
            <X size={16} />
          </button>
        </div>
      ))}
      {itinerary.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-4">No itinerary days yet. Click &quot;Add Day&quot; to start building.</p>
      )}
    </div>
  );
};

// ─── Dynamic array field helper ────────────────────────────────────────
const ArrayField = ({ label, items, onChange, placeholder }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <button type="button" onClick={() => onChange([...items, ''])} className="text-xs text-indigo-600 hover:text-indigo-800">+ Add</button>
    </div>
    {items.map((item, i) => (
      <div key={i} className="flex gap-1 mb-1">
        <input type="text" value={item} placeholder={placeholder}
          onChange={(e) => { const u = [...items]; u[i] = e.target.value; onChange(u); }}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500 outline-none" />
        {items.length > 1 && (
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
            <X size={14} />
          </button>
        )}
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════
const Tours = () => {
  const [tours, setTours] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => { fetchTours(); }, []);

  const fetchTours = async () => {
    try {
      setLoading(true); setError(null);
      const response = await getAllTours();
      const toursData = response?.data?.data?.tours || response?.data?.tours || [];
      setTours(Array.isArray(toursData) ? toursData : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch tours');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      price: Number(formData.price),
      duration: Number(formData.duration),
      groupSize: Number(formData.groupSize),
      pricing: {
        solo: Number(formData.pricing.solo) || Number(formData.price),
        group: Number(formData.pricing.group) || Math.round(Number(formData.price) * 0.85),
        minGroupSize: Number(formData.pricing.minGroupSize) || 10,
      },
      thumbnail: formData.thumbnail || '',
      images: formData.images.filter(Boolean),
      startDates: formData.startDates.filter(Boolean),
      highlights: formData.highlights.filter(Boolean),
      included: formData.included.filter(Boolean),
      notIncluded: formData.notIncluded.filter(Boolean),
      itinerary: formData.itinerary.filter(d => d.title),
      details: {
        ...formData.details,
        maxCapacity: Number(formData.details.maxCapacity) || 0,
        gearChecklist: formData.details.gearChecklist.filter(Boolean),
        safetyProtocols: formData.details.safetyProtocols.filter(Boolean),
        learningOutcomes: formData.details.learningOutcomes.filter(Boolean),
        amenities: formData.details.amenities.filter(Boolean),
      },
    };
    try {
      setSaving(true);
      if (selectedTour) {
        await updateTour(selectedTour._id, payload);
        toast.success('Tour updated');
      } else {
        await createTour(payload);
        toast.success('Tour created');
      }
      fetchTours();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err?.message || 'Failed to save tour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tour?')) return;
    try {
      await deleteTour(id);
      fetchTours();
      toast.success('Tour deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (tour) => {
    setSelectedTour(tour);
    setFormData({
      title: tour.title || '',
      description: tour.description || '',
      price: tour.price || '',
      duration: tour.duration || '',
      groupSize: tour.groupSize || '',
      location: tour.location || '',
      category: tour.category || 'customized',
      difficulty: tour.difficulty || 'moderate',
      featured: tour.featured || false,
      showOnHomepage: tour.showOnHomepage || false,
      thumbnail: tour.thumbnail || '',
      images: tour.images || [],
      startDates: tour.startDates?.length ? tour.startDates.map(d => d?.substring?.(0, 10) || d) : [''],
      highlights: tour.highlights?.length ? tour.highlights : [''],
      included: tour.included?.length ? tour.included : [''],
      notIncluded: tour.notIncluded?.length ? tour.notIncluded : [''],
      pricing: {
        solo: tour.pricing?.solo || tour.price || '',
        group: tour.pricing?.group || '',
        minGroupSize: tour.pricing?.minGroupSize || 10,
      },
      itinerary: tour.itinerary || [],
      details: {
        maxCapacity: tour.details?.maxCapacity || '',
        gearChecklist: tour.details?.gearChecklist?.length ? tour.details.gearChecklist : [''],
        safetyProtocols: tour.details?.safetyProtocols?.length ? tour.details.safetyProtocols : [''],
        learningOutcomes: tour.details?.learningOutcomes?.length ? tour.details.learningOutcomes : [''],
        studentTeacherRatio: tour.details?.studentTeacherRatio || '',
        amenities: tour.details?.amenities?.length ? tour.details.amenities : [''],
      },
    });
    setActiveTab('basic');
    setIsModalOpen(true);
  };

  const handleToggleHomepage = async (tour) => {
    try {
      await updateTour(tour._id, { showOnHomepage: !tour.showOnHomepage });
      fetchTours();
      toast.success(tour.showOnHomepage ? 'Removed from homepage' : 'Added to homepage');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const resetForm = () => {
    setSelectedTour(null);
    setFormData({
      ...emptyForm,
      thumbnail: '', images: [], startDates: [''], highlights: [''], included: [''], notIncluded: [''],
      details: { ...emptyForm.details, gearChecklist: [''], safetyProtocols: [''], learningOutcomes: [''], amenities: [''] },
    });
    setActiveTab('basic');
  };

  const filteredTours = tours.filter(tour =>
    (tour.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tour.location || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryBadge = (cat) => {
    const colors = {
      customized: 'bg-purple-100 text-purple-700', educational: 'bg-green-100 text-green-700',
      adventurous: 'bg-orange-100 text-orange-700', luxury: 'bg-amber-100 text-amber-700',
      family: 'bg-pink-100 text-pink-700', corporate: 'bg-slate-100 text-slate-700',
      adventure: 'bg-orange-100 text-orange-700', cultural: 'bg-indigo-100 text-indigo-700',
      beach: 'bg-cyan-100 text-cyan-700', mountain: 'bg-emerald-100 text-emerald-700',
      wildlife: 'bg-lime-100 text-lime-700', city: 'bg-blue-100 text-blue-700',
    };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  const setF = (field, value) => setFormData(p => ({ ...p, [field]: value }));
  const setPricing = (field, value) => setFormData(p => ({ ...p, pricing: { ...p.pricing, [field]: value } }));
  const setDetails = (field, value) => setFormData(p => ({ ...p, details: { ...p.details, [field]: value } }));

  return (
    <div>
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

      <div className="flex justify-between items-center mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input type="text" placeholder="Search tours..." className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <button onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-indigo-700" disabled={loading}>
          <Plus size={20} className="mr-2" /> Add New Tour
        </button>
      </div>

      {/* Tours Table */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin h-8 w-8 text-indigo-500 mx-auto" /></div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tour</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Homepage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTours.map((tour) => (
                <tr key={tour._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {(tour.thumbnail || tour.images?.[0]) ? (
                        <img className="h-10 w-10 rounded-lg object-cover" src={tour.thumbnail || tour.images[0]} alt={tour.title} />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center"><MapPin size={16} className="text-gray-400" /></div>
                      )}
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{tour.title}</div>
                        <div className="text-xs text-gray-500">{tour.location}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getCategoryBadge(tour.category)}`}>
                      {tour.category}
                    </span>
                    {tour.featured && <Star size={12} className="inline ml-1 text-amber-500 fill-current" />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tour.duration} days</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">₹{tour.price}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Star size={13} className="text-amber-400 fill-current" /> {tour.rating?.toFixed(1)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleToggleHomepage(tour)}
                      className={`p-1.5 rounded-lg transition-all ${
                        tour.showOnHomepage
                          ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      title={tour.showOnHomepage ? 'Remove from homepage' : 'Show on homepage'}
                    >
                      <Home size={16} />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleEdit(tour)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(tour._id)} className="text-red-600 hover:text-red-900"><Trash2 size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredTours.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-400">
              <MapPin size={40} className="mx-auto mb-2 opacity-30" />
              <p className="font-medium">No tours found</p>
            </div>
          )}
        </div>
      )}

      {/* ═══════ Add/Edit Tour Modal ═══════ */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-4xl w-full my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">{selectedTour ? 'Edit Tour' : 'Create New Tour'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b px-5 gap-1">
              {['basic', 'pricing', 'itinerary', 'details', 'media'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="p-5">
              {/* Basic Tab */}
              {activeTab === 'basic' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input type="text" value={formData.title} onChange={(e) => setF('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                    <input type="text" value={formData.location} onChange={(e) => setF('location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select value={formData.category} onChange={(e) => setF('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none">
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days) *</label>
                    <input type="number" value={formData.duration} onChange={(e) => setF('duration', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" required min={1} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group Size *</label>
                    <input type="number" value={formData.groupSize} onChange={(e) => setF('groupSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" required min={1} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                    <select value={formData.difficulty} onChange={(e) => setF('difficulty', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none">
                      <option value="easy">Easy</option>
                      <option value="moderate">Moderate</option>
                      <option value="challenging">Challenging</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-5 col-span-2">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="featured" checked={formData.featured} onChange={(e) => setF('featured', e.target.checked)} className="rounded" />
                      <label htmlFor="featured" className="text-sm font-medium text-gray-700">Featured Tour</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="showOnHomepage" checked={formData.showOnHomepage} onChange={(e) => setF('showOnHomepage', e.target.checked)} className="rounded" />
                      <label htmlFor="showOnHomepage" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        <Home size={14} /> Show on Homepage
                      </label>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea value={formData.description} onChange={(e) => setF('description', e.target.value)}
                      rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none resize-none" required />
                  </div>
                  <div className="col-span-2"><ArrayField label="Highlights" items={formData.highlights} onChange={(v) => setF('highlights', v)} placeholder="e.g. Visit the Taj Mahal" /></div>
                  <div><ArrayField label="Included" items={formData.included} onChange={(v) => setF('included', v)} placeholder="e.g. Airport transfers" /></div>
                  <div><ArrayField label="Not Included" items={formData.notIncluded} onChange={(v) => setF('notIncluded', v)} placeholder="e.g. Personal expenses" /></div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="grid grid-cols-2 gap-4 max-w-lg">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (per person) *</label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="number" value={formData.price} onChange={(e) => setF('price', e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" required min={0} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Solo Price</label>
                    <input type="number" value={formData.pricing.solo} onChange={(e) => setPricing('solo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" placeholder="Auto from base price" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group Price (per person)</label>
                    <input type="number" value={formData.pricing.group} onChange={(e) => setPricing('group', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" placeholder="Auto 15% discount" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Group Size</label>
                    <input type="number" value={formData.pricing.minGroupSize} onChange={(e) => setPricing('minGroupSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" min={2} />
                  </div>
                  <div className="col-span-2 mt-2 bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                    <strong>Tip:</strong> Group price kicks in at {formData.pricing.minGroupSize || 10}+ guests.
                  </div>
                </div>
              )}

              {/* Itinerary Tab */}
              {activeTab === 'itinerary' && (
                <ItineraryBuilder itinerary={formData.itinerary} onChange={(v) => setF('itinerary', v)} />
              )}

              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity</label>
                    <input type="number" value={formData.details.maxCapacity} onChange={(e) => setDetails('maxCapacity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none max-w-xs" />
                  </div>
                  {(formData.category === 'adventurous' || formData.category === 'adventure') && (
                    <ArrayField label="Gear Checklist" items={formData.details.gearChecklist} onChange={(v) => setDetails('gearChecklist', v)} placeholder="e.g. Hiking boots" />
                  )}
                  {formData.category === 'educational' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Student-Teacher Ratio</label>
                        <input type="text" value={formData.details.studentTeacherRatio} onChange={(e) => setDetails('studentTeacherRatio', e.target.value)}
                          placeholder="e.g. 10:1" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none max-w-xs" />
                      </div>
                      <ArrayField label="Learning Outcomes" items={formData.details.learningOutcomes} onChange={(v) => setDetails('learningOutcomes', v)} placeholder="e.g. Understand local history" />
                      <ArrayField label="Safety Protocols" items={formData.details.safetyProtocols} onChange={(v) => setDetails('safetyProtocols', v)} placeholder="e.g. First aid certified guides" />
                    </>
                  )}
                  {formData.category === 'luxury' && (
                    <ArrayField label="Amenities" items={formData.details.amenities} onChange={(v) => setDetails('amenities', v)} placeholder="e.g. Private suite" />
                  )}
                </div>
              )}

              {/* Media & Dates Tab */}
              {activeTab === 'media' && (
                <div className="space-y-6 max-w-3xl">
                  <ImageUploader label="Thumbnail (Tour Card Image)" value={formData.thumbnail} onChange={(v) => setF('thumbnail', v)}
                    hint="Main image shown on tour cards and listings. Upload or paste URL." />
                  <div className="border-t pt-4">
                    <MultiImageUploader label="Gallery Images (Tour Detail Page)" images={formData.images} onChange={(v) => setF('images', v)}
                      hint="These images are shown when someone opens the tour detail page. Upload multiple at once or add URLs." />
                  </div>
                  <div className="border-t pt-4">
                    <ArrayField label="Start Dates" items={formData.startDates} onChange={(v) => setF('startDates', v)} placeholder="YYYY-MM-DD" />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-2">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : (selectedTour ? 'Update Tour' : 'Create Tour')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tours;